import React from "react";
import { ClassNameProps, If, makeClassName, Row, StyleProps } from "@hex-insights/core";
import { Badge, BadgeProps } from "../../../../Components/Badge";
import { Icon } from "../../../../Components/Icon";
import { truncateDisplayItems, useDynamicTruncation, User, UserNameFragment } from "../../../../Utilities";
import { UserFormat } from "../../../Format";
import * as UserComponents from "../../User";
import styles from "./styles.module.css";

type PublicBadgeProps = Omit<BadgeProps, "children">;

export function PublicBadge({ size, className, ...props }: PublicBadgeProps) {
	return (
		<Badge
			{...props}
			size={size}
			className={makeClassName(styles["public-badge"], styles[`public-badge--${size}`], className)}
		>
			<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
				<Icon.Globe /> Public
			</Row>
		</Badge>
	);
}

type AttendeesRowProps = {
	users: (Pick<User, "id"> & UserComponents.IconUser & UserNameFragment)[];
	numAdditional?: number;
	horizontalSpacing?: number;
	maxDisplayed?: number;
	iconSize: number;
} & Partial<ClassNameProps & StyleProps>;

export function AttendeesRow({
	users,
	numAdditional = 0,
	horizontalSpacing = 0,
	maxDisplayed: maxDisplayedProp,
	iconSize,
	className,
	style,
}: AttendeesRowProps) {
	const ref = React.useRef<HTMLDivElement | null>(null);
	const { maxDisplayed: dynamicMaxDisplayed } = useDynamicTruncation(
		ref,
		(container) => container?.firstChild?.firstChild as HTMLElement | null | undefined,
		{ gapSize: horizontalSpacing },
	);
	const maxDisplayed = maxDisplayedProp
		? Math.min(maxDisplayedProp, dynamicMaxDisplayed ?? maxDisplayedProp)
		: dynamicMaxDisplayed;
	const { displayedItems, anyHidden, numHidden } = truncateDisplayItems(users, maxDisplayed, { numAdditional });

	return (
		<div ref={ref} className={className} style={style}>
			<Row justify="spaced-start" horizontalSpacing={`${horizontalSpacing}px`}>
				{displayedItems.map((user) => (
					<UserComponents.UserIcon key={user.id} user={user} title={UserFormat.name(user)} size={iconSize} />
				))}
				<If condition={anyHidden || numAdditional > 0}>
					<UserComponents.UserAdditionalCountIcon count={numHidden + numAdditional} size={iconSize} />
				</If>
			</Row>
		</div>
	);
}
